/**
 * @file JS - Controller - GisController.js
 * @author Adam Lemire
 * @since 2019/06/08
 * @version 1.0
 * @summary Dernières modifications : 2022/05/12
 */

import $ from 'jquery';
import ProjectController from './ProjectController.js';
import LeftMenuController from './LeftMenuController.js';
import { gisSettings, WebRasterLayer } from './../gisSettings.js';
import Gis from './../classes/Gis.js';
import { GisButton, GisSection, ContextMenu, ProjectMenu } from './../helpers/Selectors.js';
import { setText } from './../helpers/languageDictionary.js';
import { hideContextMenus } from './../helpers/GlobalFunctions.js';
import { MultiMapPageFormat } from '../helpers/Constants.js';
import { Evenements, reinitialiserEvenements } from '../events/Evenements.js';
import LayoutPageController from './Page/LayoutPageController.js';

const OPTION_MENU_HAUT = gisSettings.topMenu.active;
const OPTION_MENU_GAUCHE = gisSettings.leftMenu.active;
const OPTION_MENU_PROJET = gisSettings.topMenu.projectMenu.active;
const OPTION_EXPORTER_CARTE = gisSettings.options.map.export.active;
const OPTION_SAUVEGARDE_PROJET_LOCAL = gisSettings.options.project.sauvegardeLocale.active;
const OPTION_SAUVEGARDE_PROJET_API = gisSettings.options.project.sauvegardeAPI.active;
const OPTION_AJOUTER_NOUVELLE_CARTE = gisSettings.options.map.addNewMap.active;
const OPTION_CONTROLES_CARTE = gisSettings.topMenu.mapControls.active;
const OPTION_MENU_HAUT_BOUTON_PDF = gisSettings.topMenu.exportPdfButton.active;
const OPTION_MENU_HAUT_BOUTON_AIDE = gisSettings.topMenu.helpButton.active;

/**
 * Controlleur du SIG
 */
export default class GisController {
  /**
   * @example const gisController = new GisController( 'iBalsac 1.0' );
   * @example gisController.activer();
   * @param { !string } version - Gis version.
   */
  constructor (version) {
    /** @type { Gis } */
    this._gis = new Gis(version);
    /** @type { string } */
    this._langue = this._obtenirLangueAffichageNavigateur();
    this._controleurProjet = new ProjectController(version);
    this._controleurExportationVersImage = new LayoutPageController();
    this._enAttenteAfficherCouchesWeb = false;
    this._chronometreRedimensionnement = 0;
  }

  activer () {
    this._gis.projet = this._controleurProjet.projet;
    this._controleurProjet.activer();
    this._controleurExportationVersImage.activer();
    this._inscrireAuditeurs();
    this._inscrireAuditeursParametresSIG();
    this._assignerLangueAffichageInitiale();
  }

  _inscrireAuditeurs () {
    this._inscrireAuditeursChangementTailleElementsDansCarte();
    this._ecouterClicFenetre();
    this._ecouterChangementProjetActif();
    this._ecouterTexteAjoute();
    this._ecouterPageCarteAffichee();
    this._ecouterFermetureMiseEnForme();
  }

  _assignerLangueAffichageInitiale () {
    if (this._langue !== 'fr') this._langue = 'en';
    setText(this._langue);
  }

  /*
   * GIS SETTINGS
   */

  _inscrireAuditeursParametresSIG () {
    const _this = this;
    if (OPTION_MENU_HAUT) {
      this._ecouterBoutonsMenuHaut();
    }
    if (OPTION_MENU_GAUCHE) {
      this._inscrireAuditeursMenuGauche();
    }
    if (OPTION_EXPORTER_CARTE) {
      $(ProjectMenu.BOUTON_EXPORTER).on('click tap', () => _this._exporterCarte());
    }
  }

  /*
   * TOP MENU
   */

  _ecouterBoutonsMenuHaut () {
    const _this = this;
    const gis = this._gis;

    if (OPTION_MENU_PROJET) {
      $(GisButton.GIS_PROJECT_MENU).on('click tap', (e) => _this._afficherMenuProjet(e));
    }
    if (OPTION_SAUVEGARDE_PROJET_LOCAL) {
      $(GisButton.PROJECT_SAVE).on('click tap', () => _this._controleurProjet.exportToJsonFile());
    }
    if (OPTION_SAUVEGARDE_PROJET_API) {
      $(GisButton.PROJECT_SAVE_API).on('click tap', () => _this._controleurProjet.exporterProjetVersAPI());
    }
    if (OPTION_AJOUTER_NOUVELLE_CARTE) {
      $(GisButton.PROJECT_ADD_SINGLE_MAP).on('click tap', () => _this._controleurProjet.ajouterPageCarteUnique());
    }
    if (OPTION_CONTROLES_CARTE) {
      $(GisButton.MAP_ZOOM_OUT).on('click tap', () => gis.projet.activeMapPage.zoomOut());
      $(GisButton.MAP_CENTER_MAP).on('click tap', () => gis.projet.activeMapPage.centerMap());
      $(GisButton.MAP_ZOOM_IN).on('click tap', () => gis.projet.activeMapPage.zoomIn());
    }
    if (OPTION_MENU_HAUT_BOUTON_PDF && OPTION_EXPORTER_CARTE) {
      $(GisButton.MAP_EXPORT_AS_IMAGE).on('click tap', () => _this._controleurExportationVersImage.afficherPlanExportation(gis.projet.activeMapPage)); //! voir si garde toujours la meme en memoire
    }
    if (OPTION_MENU_HAUT_BOUTON_AIDE) {
      $(GisButton.GIS_HELP).on('click tap', () => gis.afficherAide());
    }

    $(GisButton.GIS_LANGUAGE).on('click tap', () => _this._basculerLangueAffichage());
    $('.top-menu__render-button').on('click tap', () => gis.projet.activeMapPage.olMap.render());
    $('.top-menu__add-multimap-page-button').on('click tap', () => _this._controleurProjet.ajouterPageCartesMultiples(MultiMapPageFormat.HORIZONTAL));
  }

  _basculerLangueAffichage () {
    const _this = this;
    if (this._langue === 'en') {
      this._langue = 'fr';
    } else {
      this._langue = 'en';
    }
    setText(this._langue);
    window.localStorage.setItem('lang', _this._langue);
    Evenements.langueAffichageChangee.notify();
  }

  _afficherMenuProjet (evt) {
    if ($(ContextMenu.PROJECT_MENU).hasClass('hidden')) {
      evt.stopPropagation();
      hideContextMenus();
      $(GisButton.GIS_PROJECT_MENU).toggleClass('active');
      $(ContextMenu.PROJECT_MENU).toggleClass('hidden');
    }
  }

  /*
   * MENU VERTICAL GAUCHE
   * @todo flex à la place?
   */
  _inscrireAuditeursMenuGauche () {
    if (gisSettings.leftMenu.mapLayers.active) {
      $(GisButton.GIS_MAP_LAYER).on('click tap', () => this._afficherCouchesCarteActive());
    }
    if (gisSettings.leftMenu.webLayers.active) {
      this._ecouterChargementListeCouchesWeb();
      this._ecouterErreurChargementListeCouchesWeb();
      $(GisButton.GIS_WEB_LAYER).on('click tap', () => this._afficherListeCouchesWeb());
    }
    if (gisSettings.leftMenu.multimapLayers.active) {
      $(GisButton.GIS_MULTI_MAPS).on('click tap', () => LeftMenuController.afficherDétailsPageCartesMultiples());
    }
    if (gisSettings.leftMenu.printLayout.active && OPTION_EXPORTER_CARTE) {
      $(GisButton.GIS_PRINT_LAYOUT).on('click tap', () => this._afficherMiseEnForme());
    }
    LeftMenuController.écouterBoutonsMenuGauche();
  }

  _afficherCouchesCarteActive () {
    this._controleurExportationVersImage.fermerMiseEnForme();
    LeftMenuController.afficherCouchesDeCarteActive();
  }

  _ecouterChargementListeCouchesWeb () {
    const gis = this._gis;
    gis.écouterListeCouchesServeurPrete(function (webLayersArray) {
      $(GisSection.WEB_LAYERS).empty();
      _créerDivCouchesWeb(webLayersArray);
      _créerDivCoucheRasterWeb();
    });

    /** @param { object<Layer> } layers */
    function _créerDivCouchesWeb (layers) {
      layers.forEach(function (element, index) {
        // if (element.Title.includes('_br')) return;
        $(GisSection.WEB_LAYERS).append('<div class="web-layers__layer web-layer-' + index + '">' +
          '<div class="web-layer__icon web-layer__icon--' + element.Style[0].Identifier + '"></div>' +
          '<div class="web-layer__text web-layer-' + index + '__text">' + element.Title + '</div>' +
          '<button class="web-layer__button web-layer-' + index + '__button">+</button>' +
          '</div>');
        _écouterBoutonAjouterCouche(element, index);
      });
    }

    /**
     * @todo faire distinction entre polygone, line, pts
     * @param {element} element
     * @param {number} index
     */
    function _écouterBoutonAjouterCouche (element, index) {
      const button = '.web-layer-' + index + '__button';
      $(button).on('click tap', () => gis.projet.activeMapPage.addVectorLayer(element.Title, gisSettings.geoserver.workspace.cieq.nom, element.Title));
    }

    /** */
    function _créerDivCoucheRasterWeb () {
      for (const key in WebRasterLayer) {
        const buttonClass = '.web-raster-layer-' + key + '__button';
        $(GisSection.WEB_LAYERS).append('<div class="web-layers__layer web-raster-layer">' +
          '<div class="web-layer__icon web-layer__icon--raster"></div>' +
          '<div class="web-layer__text web-raster-layer__text">' + WebRasterLayer[key].name + '</div>' +
          '<button class="web-layer__button web-raster-layer-' + key + '__button">+</button>' +
          '</div>');

        $(buttonClass).on('click tap', () => gis.projet.activeMapPage.addRasterLayer(WebRasterLayer[key]));
      }
    }
  }

  _ecouterErreurChargementListeCouchesWeb () {
    const _this = this;
    Evenements.erreurObtenirListeCouchesServeur.subscribe(function () {
      $(GisSection.WEB_LAYERS).empty();
      $(GisSection.WEB_LAYERS).append('<button class="web-layer__refresh-button" data-text="button.retry"></button>');
      $('.web-layer__refresh-button').on('click tap', function () {
        $('.web-layer__refresh-button').off('click tap');
        _this._gis.obtenirListeCouchesServeur();
      });
    });
  }

  _afficherListeCouchesWeb () {
    if ($(GisButton.GIS_PRINT_LAYOUT).hasClass('active')) {
      this._enAttenteAfficherCouchesWeb = true;
      this._controleurExportationVersImage.fermerMiseEnForme();
    } else {
      LeftMenuController.afficherCouchesWeb();
    }
    console.log('afficher couche web');
  }

  _afficherOptionsCarteMulti () {
    this._controleurExportationVersImage.fermerMiseEnForme();
    LeftMenuController.afficherDétailsPageCartesMultiples();
  }

  _afficherMiseEnForme () {
    LeftMenuController.afficherOptionsMiseEnForme();
    this._controleurExportationVersImage.afficherPlanExportation(this._gis.projet.activeMapPage);
  }

  /*
   * Global
   */

  /** @returns { string } */
  _obtenirLangueAffichageNavigateur () {
    const lang = window.localStorage.getItem('lang') || navigator.language.slice(0, 2);
    if (!window.localStorage.getItem('lang')) {
      window.localStorage.setItem('lang', lang);
    }
    return lang;
  }

  _ecouterClicFenetre () {
    $(window).on('click tap', (e) => {
      hideContextMenus();
    });
  }

  /**
   * @todo ne marche probablement pas, doit passer un parametre pour que ce soit a jour (07/10/2020 ?? semble fonctionner, à tester)
   */
  _inscrireAuditeursChangementTailleElementsDansCarte () {
    const _this = this;
    const gis = this._gis;

    window.onresize = function () {
      setTimeout(function () {
        gis.projet.activeMapPage.refreshView();
      }, 100);

      clearTimeout(_this._chronometreRedimensionnement);
      _this._chronometreRedimensionnement = setTimeout(_this._notifierRedimensionnementFenetre, 100);
    };

    $(GisSection.CONTENT).on('classChange', async function () {
      if (typeof globalIdProjet === 'undefined') {
        gis.projet.activeMapPage.refreshView();
      } else if (gis.projet.activeMapPage) {
        const pageActive = await gis.projet.activeMapPage;
        pageActive.refreshView();
      }
    });

    if (gisSettings.mapTabsBar.active) {
      $(GisSection.MAP_TABS.TAB).on('click tap', function () {
        gis.projet.activeMapPage.refreshView();
      });
    }
  }

  _notifierRedimensionnementFenetre () {
    Evenements.redimensionnementFenetre.notify();
  }

  _ecouterChangementProjetActif () {
    const _this = this;
    Evenements.changementProjetActif.subscribe(function (projet) {
      _this._gis.projet = projet;
      reinitialiserEvenements();
    });
  }

  _ecouterTexteAjoute () {
    const _this = this;
    Evenements.texteAjoute.subscribe(function () {
      setText(_this._langue);
    });
  }

  _ecouterPageCarteAffichee () {
    const _this = this;
    Evenements.pageCarteAffichee.subscribe(function () {
      _this._controleurExportationVersImage.fermerMiseEnForme();
    });
  }

  _ecouterFermetureMiseEnForme () {
    const _this = this;
    Evenements.fermetureMiseEnForme.subscribe(function () {
      if (_this._enAttenteAfficherCouchesWeb) {
        LeftMenuController.afficherCouchesWeb();
        _this._enAttenteAfficherCouchesWeb = false;
      } else {
        console.log('_ecouterFermetureMiseEnForme');
        LeftMenuController.afficherCouchesDeCarteActive();
      }
    });
  }

  async _exporterCarte () {
    const pagePrete = gisSettings.leftMenu.printLayout.active
      ? this._afficherMiseEnForme()
      : this._controleurExportationVersImage.afficherPlanExportation(this._gis.projet.activeMapPage);
    this._controleurExportationVersImage.exporterCarte(await this._controleurExportationVersImage.pageLayout, pagePrete);
  }
}
